import {
  Component,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { HasPermissionDirective } from '../../core/directives/has-permission.directive';
import {
  Payment,
  PaymentReceipt,
  PaymentService
} from '../../core/services/payment.service';
import { TenantContextService } from '../../core/services/tenant-context.service';
import { BusinessUnitStore } from '../../core/stores/business-unit.store';
import { NoBuSelectedComponent } from '../../shared/components/no-bu-selected/no-bu-selected.component';
import { formatSlDateTime } from '../../core/utils/datetime.util';
import { PaymentFormComponent } from './payment-form/payment-form.component';
import { ReceiptViewComponent } from './receipt-view/receipt-view.component';
import { LoaderComponent } from '../../shared/components/loader/loader.component';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';
import { RecycleBinModalComponent } from '../../shared/components/recycle-bin-modal/recycle-bin-modal.component';
import { AuthService } from '../../core/services/auth.service';
import { MemberDuesAlertComponent } from '../../shared/components/member-dues-alert/member-dues-alert.component';

@Component({
  selector: 'app-payments',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, HasPermissionDirective, PaymentFormComponent, ReceiptViewComponent, NoBuSelectedComponent, LoaderComponent, ConfirmDialogComponent, RecycleBinModalComponent, MemberDuesAlertComponent],
  templateUrl: './payments.component.html'
})
export class PaymentsComponent implements OnInit, OnDestroy {
  private paymentService = inject(PaymentService);
  private tenantContext = inject(TenantContextService);
  private buStore = inject(BusinessUnitStore);
  readonly auth = inject(AuthService);

  showRecycleBin = signal(false);

  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  payments: Payment[] = [];
  totalCount = 0;
  totalPages = 0;
  currentPage = 1;
  pageSize = 20;

  isLoading = false;
  errorMessage = '';

  searchTerm = '';
  filterMonth: number | null = null;
  filterYear: number | null = null;

  showFormModal = false;
  editingPayment: Payment | null = null;

  pendingDeletePayment: Payment | null = null;
  isDeleting = false;

  showReceipt = false;
  currentReceipt: PaymentReceipt | null = null;
  isLoadingReceipt = false;

  months = [
    { value: 1, name: 'January' },   { value: 2, name: 'February' },
    { value: 3, name: 'March' },     { value: 4, name: 'April' },
    { value: 5, name: 'May' },       { value: 6, name: 'June' },
    { value: 7, name: 'July' },      { value: 8, name: 'August' },
    { value: 9, name: 'September' }, { value: 10, name: 'October' },
    { value: 11, name: 'November' }, { value: 12, name: 'December' }
  ];

  years: number[] = [];

  readonly selectedBusinessUnitId = this.tenantContext.selectedBusinessUnitId;
  readonly hasBuSelected = computed(() => this.selectedBusinessUnitId() != null);

  constructor() {
    const currentYear = new Date().getFullYear();
    for (let i = currentYear - 5; i <= currentYear + 1; i++) this.years.push(i);

    // Reload only when the selected business unit actually changes after mount — effect()
    // always runs once on its first flush regardless of when it's created, so tracking the
    // previous value explicitly (rather than a flag set in ngOnInit) makes that first run a
    // real no-op instead of duplicating ngOnInit's initial load.
    let previousBuId = this.tenantContext.selectedBusinessUnitId();
    effect(() => {
      const buId = this.tenantContext.selectedBusinessUnitId();
      if (buId === previousBuId) return;
      previousBuId = buId;
      this.currentPage = 1;
      this.loadPayments();
    });
  }

  ngOnInit(): void {
    this.buStore.loadOnce();
    this.loadPayments();

    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.currentPage = 1;
      this.loadPayments();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadPayments(): void {
    if (!this.hasBuSelected()) {
      this.payments = [];
      this.totalCount = 0;
      this.totalPages = 0;
      this.isLoading = false;
      this.errorMessage = '';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    this.paymentService.getPayments({
      search: this.searchTerm || undefined,
      month: this.filterMonth || undefined,
      year: this.filterYear || undefined,
      excludeEventPayments: true,
      page: this.currentPage,
      pageSize: this.pageSize
    }).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          const raw = res.data as any;
          if (Array.isArray(raw)) {
            this.payments = raw;
            this.totalCount = raw.length;
            this.totalPages = 1;
          } else {
            this.payments = raw.data || [];
            this.totalCount = raw.totalCount || 0;
            this.totalPages = raw.totalPages || 0;
          }
        } else {
          this.errorMessage = res.message || 'Failed to load payments.';
          this.payments = [];
          this.totalCount = 0;
          this.totalPages = 0;
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.errorMessage = err?.error?.message || 'Failed to load payments.';
        this.isLoading = false;
      }
    });
  }

  onSearchChange(): void { this.searchSubject.next(this.searchTerm); }
  onFilterChange(): void { this.currentPage = 1; this.loadPayments(); }

  changePage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;
    this.currentPage = page;
    this.loadPayments();
  }

  get pages(): number[] {
    const arr: number[] = [];
    const start = Math.max(1, this.currentPage - 2);
    const end = Math.min(this.totalPages, start + 4);
    for (let i = start; i <= end; i++) arr.push(i);
    return arr;
  }

  get startIndex(): number {
    return this.totalCount === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
  }

  get endIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalCount);
  }

  openCreate(): void {
    if (!this.hasBuSelected()) {
      this.errorMessage = 'Select a committee from the top bar before recording payments.';
      return;
    }
    this.editingPayment = null;
    this.showFormModal = true;
  }

  openEdit(payment: Payment): void {
    this.editingPayment = payment;
    this.showFormModal = true;
  }

  closeForm(): void {
    this.showFormModal = false;
    this.editingPayment = null;
  }

  onPaymentSaved(_payment: Payment): void {
    this.closeForm();
    this.loadPayments();
  }

  confirmDelete(payment: Payment): void {
    if (payment.id == null) return;
    this.pendingDeletePayment = payment;
  }

  cancelDelete(): void {
    if (this.isDeleting) return;
    this.pendingDeletePayment = null;
  }

  performDelete(): void {
    const id = this.pendingDeletePayment?.id;
    if (id == null) return;
    this.isDeleting = true;
    this.paymentService.deletePayment(id).subscribe({
      next: (res) => {
        this.isDeleting = false;
        if (res.success) {
          this.pendingDeletePayment = null;
          this.loadPayments();
        } else {
          this.errorMessage = res.message || 'Delete failed.';
        }
      },
      error: () => {
        this.isDeleting = false;
        this.errorMessage = 'Delete failed.';
      }
    });
  }

  pendingDeleteLabel(): string {
    const p = this.pendingDeletePayment;
    if (!p) return '';
    const who = p.memberName ? `${p.memberName}` : `Member #${p.memberId}`;
    return `Rs ${p.amount.toLocaleString()} from ${who}`;
  }

  openReceipt(payment: Payment): void {
    if (payment.id == null) return;
    this.isLoadingReceipt = true;
    this.paymentService.getReceipt(payment.id).subscribe({
      next: (res) => {
        this.isLoadingReceipt = false;
        if (res.success && res.data) {
          this.currentReceipt = res.data;
          this.showReceipt = true;
        } else {
          this.errorMessage = res.message || 'Failed to load receipt.';
        }
      },
      error: () => {
        this.isLoadingReceipt = false;
        this.errorMessage = 'Failed to load receipt.';
      }
    });
  }

  closeReceipt(): void {
    this.showReceipt = false;
    this.currentReceipt = null;
  }

  monthName(m: number): string {
    return this.months.find(x => x.value === m)?.name || `${m}`;
  }

  eventName(payment: Payment): string | null {
    if (!payment.eventContributionId) return null;
    const ref = payment.reference || '';
    const prefix = 'Contribution to ';
    return ref.startsWith(prefix) ? ref.substring(prefix.length) : (ref || null);
  }

  formatDate(d: string | null | undefined): string {
    return formatSlDateTime(d);
  }
}
